import api from "./api";
import {
  ISale,
  ICartProduct,
  ISaleProduct,
  ISaleProductExtra,
  ISaleProductFlavour,
  ISaleProductFlavourExtra,
} from "../classes/interfaces";

/**
 * Posts a sale to the API. Unlike storeService's functions, this does NOT throw
 * on a non-200 response — the caller needs to branch on the specific failure
 * reason (e.g. `res.data.status === "store closed"`), which a generic
 * throw-on-failure helper would obscure. Callers must check the returned
 * body's `code` themselves, exactly as the pre-migration inline code did.
 */
export async function postSale(sale: ISale): Promise<any> {
  const res = await api().post("/sale", sale);
  return res.data;
}

/**
 * Transforms cart products (ICartProduct[]) into the shape the /sale endpoint expects
 * (ISaleProduct[]) — handling the size-vs-product distinction, extras, and nested
 * flavours. Extracted from a block that is byte-identical in Cart/index.tsx and
 * Payments/index.tsx; both pages will be migrated to call this instead of duplicating
 * it, in later tasks of this modularization plan.
 */
export function buildSaleProducts(products: ICartProduct[]): ISaleProduct[] {
  const result: ISaleProduct[] = [];

  products.forEach((product) => {
    const newProduct: ISaleProduct = {
      amount: product.amount,
      price: product.price,
      discount: 0,
      addition: 0,
      extras: [],
      flavours: [],
      note: (product.obs || "").trim(),
    };

    if (product.sizeId && product.sizeId !== null) {
      newProduct.size = { id: product.sizeId };
    } else {
      newProduct.product = { id: product.product_id };
    }

    product.extras.forEach((extra) => {
      const newExtra: ISaleProductExtra = {
        extra: { id: extra.id },
        amount: extra.amount,
        price: extra.price,
      };
      if (extra.stepId !== undefined) {
        newExtra.step = { id: extra.stepId };
      }
      newProduct.extras.push(newExtra);
    });

    if (product.flavours) {
      product.flavours.forEach((flavour) => {
        const newFlavour: ISaleProductFlavour = {
          product: { id: flavour.product_id },
          amount: flavour.amount,
          price: flavour.price,
          addition: 0.0,
          discount: 0.0,
          note: flavour.obs,
          extras: [],
        };
        flavour.extras.forEach((extra) => {
          const newExtra: ISaleProductFlavourExtra = {
            extra: { id: extra.id },
            amount: extra.amount,
            price: extra.price,
          };
          newFlavour.extras.push(newExtra);
        });
        newProduct.flavours.push(newFlavour);
      });
    }

    result.push(newProduct);
  });

  return result;
}
